"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const types_1 = require("../../../config/types");
const onchainInteractionsCollateral_1 = __importDefault(require("./onchainInteractionsCollateral"));
const onchainInteractionsBorrowable_1 = __importDefault(require("./onchainInteractionsBorrowable"));
const utils_1 = require("../../../utils");
const general_1 = require("../../../config/general");
class OnchainInteractionsLendingPool {
    constructor(interactions, lendingPool) {
        this.getInteractions = () => this.interactions;
        this.getLendingPool = () => this.lendingPool;
        // Shortcuts
        this.getBorrowableA = () => this.poolTokens[types_1.PoolTokenType.BorrowableA];
        this.getBorrowableB = () => this.poolTokens[types_1.PoolTokenType.BorrowableB];
        this.getCollateral = () => this.poolTokens[types_1.PoolTokenType.Collateral];
        this.getFactory = () => this.lendingPool.getImpermaxFactory().getFactory();
        this.getPairAddress = () => this.lendingPool.getPairAddress();
        this.getRouterContract = () => this.lendingPool.getImpermaxFactory().getRouter();
        this.getAccountAddress = () => this.interactions.getAccountAddress();
        this.send = (method, onTransactionHash) => this.interactions.send(method, onTransactionHash);
        this.interactions = interactions;
        this.lendingPool = lendingPool;
        this.poolTokens = {
            [types_1.PoolTokenType.Collateral]: new onchainInteractionsCollateral_1.default(this),
            [types_1.PoolTokenType.BorrowableA]: new onchainInteractionsBorrowable_1.default(this, types_1.PoolTokenType.BorrowableA),
            [types_1.PoolTokenType.BorrowableB]: new onchainInteractionsBorrowable_1.default(this, types_1.PoolTokenType.BorrowableB),
        };
    }
    getPoolToken(poolTokenType) {
        return this.poolTokens[poolTokenType];
    }
    getAccountLendingPool() {
        return this.interactions.getAccount().getLendingPool(this.getFactory(), this.getPairAddress());
    }
    getLeverageAmounts(leverage, slippage) {
        return __awaiter(this, void 0, void 0, function* () {
            const [priceA, priceB] = yield this.lendingPool.getMarketPriceDenomLP();
            // This function must use the market price, but the account leverage is calculated with the TWAP, so we need an adjustFactor
            const [priceATWAP, priceBTWAP] = yield this.lendingPool.getPriceDenomLP();
            const diff = priceA > priceATWAP ? priceA / priceATWAP : priceATWAP / priceA;
            const adjustFactor = Math.pow((0, utils_1.impermanentLoss)(Math.pow(diff, 2)), leverage);
            const accountLendingPool = yield this.getAccountLendingPool();
            const currentLeverage = yield accountLendingPool.getLeverage();
            const collateralValue = yield accountLendingPool.getCollateral().getDeposited();
            const changeCollateralValue = (collateralValue * leverage / currentLeverage - collateralValue) * adjustFactor;
            const [reservesWeightA, reservesWeightB] = yield this.lendingPool.getMarketReservesWeights();
            const changeValueA = changeCollateralValue * reservesWeightA;
            const changeValueB = changeCollateralValue * reservesWeightB;
            const bAmountA = priceA > 0 ? changeValueA / priceA : 0;
            const bAmountB = priceB > 0 ? changeValueB / priceB : 0;
            const cAmount = changeCollateralValue ? changeCollateralValue : 0;
            return {
                bAmountA: bAmountA,
                bAmountB: bAmountB,
                cAmount: cAmount,
                bAmountAMin: bAmountA / slippage,
                bAmountBMin: bAmountB / slippage,
                cAmountMin: cAmount / Math.sqrt(slippage),
            };
        });
    }
    leverage(amountA, amountB, amountAMin, amountBMin, permitDataA, permitDataB, onTransactionHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const router = this.getRouterContract();
            const dataA = permitDataA ? permitDataA.permitData : '0x';
            const dataB = permitDataB ? permitDataB.permitData : '0x';
            if (permitDataA && permitDataB && !permitDataA.deadline.eq(permitDataB.deadline))
                return console.error("Permits deadline are not equal");
            const deadline = permitDataA ? permitDataA.deadline : permitDataB ? permitDataB.deadline : general_1.DEADLINE;
            const pairAddress = this.getPairAddress();
            const account = this.getAccountAddress();
            return this.send(router.methods.leverage(pairAddress, amountA, amountB, amountAMin, amountBMin, account, deadline, dataA, dataB), onTransactionHash);
        });
    }
    getDeleverageAmounts(changeCollateralValue, slippage) {
        return __awaiter(this, void 0, void 0, function* () {
            changeCollateralValue = changeCollateralValue !== null && changeCollateralValue !== void 0 ? changeCollateralValue : 0;
            const [priceA, priceB] = yield this.lendingPool.getMarketPriceDenomLP();
            const [reservesWeightA, reservesWeightB] = yield this.lendingPool.getMarketReservesWeights();
            const changeValueA = changeCollateralValue * reservesWeightA;
            const changeValueB = changeCollateralValue * reservesWeightB;
            const bAmountA = priceA > 0 ? changeValueA / priceA : 0;
            const bAmountB = priceB > 0 ? changeValueB / priceB : 0;
            return {
                bAmountA: bAmountA,
                bAmountB: bAmountB,
                cAmount: changeCollateralValue,
                bAmountAMin: bAmountA / Math.sqrt(slippage),
                bAmountBMin: bAmountB / Math.sqrt(slippage),
            };
        });
    }
    deleverage(tokens, amountAMin, amountBMin, permitData, onTransactionHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const router = this.getRouterContract();
            const data = permitData ? permitData.permitData : '0x';
            const deadline = permitData ? permitData.deadline : general_1.DEADLINE;
            const pairAddress = this.getPairAddress();
            return this.send(router.methods.deleverage(pairAddress, tokens, amountAMin, amountBMin, deadline, data), onTransactionHash);
        });
    }
    trackBorrows(onTransactionHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const account = this.getAccountAddress();
            const claimAggregator = this.interactions.getOnchain().getContractHelper().claimAggregator;
            const toTrack = [];
            const accountLendingPool = yield this.getAccountLendingPool();
            for (const b of [accountLendingPool.getBorrowableA(), accountLendingPool.getBorrowableB()]) {
                if ((yield b.getBorrowed()) === 0 || (yield b.getFarmingShares()) > 0)
                    continue;
                toTrack.push(yield b.getPoolToken().getPoolTokenAddress());
            }
            return this.send(claimAggregator.methods.trackBorrows(account, toTrack), onTransactionHash);
        });
    }
    reinvest(onTransactionHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const stakedLPToken = yield this.lendingPool.getStakedLPToken();
            return this.send(stakedLPToken.methods.reinvest(), onTransactionHash);
        });
    }
}
exports.default = OnchainInteractionsLendingPool;
